import { isNormalizedError, type NormalizedError } from '../../services/errorNormalizer'
import { cn } from '../../utils/cn'
import { Button } from './Button'

export interface InlineErrorProps {
  /** A NormalizedError, an RTK Query error, or a plain string. */
  error: unknown
  onRetry?: () => void
  className?: string
}

function toNormalized(error: unknown): NormalizedError | null {
  if (!error) return null
  if (typeof error === 'string') return { status: 'CLIENT', message: error }
  if (isNormalizedError(error)) return error
  return { status: 'UNKNOWN', message: 'Something went wrong. Please try again.' }
}

/**
 * Renders the customer-safe message from errorNormalizer. The field bag is not
 * repeated here — those messages belong on the fields themselves — except when
 * a 422 arrives with no field the form actually renders.
 */
export function InlineError({ error, onRetry, className }: InlineErrorProps) {
  const normalized = toNormalized(error)
  if (!normalized) return null

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-error/30 bg-error-container px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <p className="flex items-start gap-2 font-body-md text-body-md text-on-error-container">
        <span className="material-symbols-outlined mt-0.5 text-[18px]">error</span>
        <span>{normalized.message}</span>
      </p>
      {onRetry && (
        <Button variant="text" size="sm" onClick={onRetry} className="self-start text-on-error-container sm:self-auto">
          Try again
        </Button>
      )}
    </div>
  )
}
